import React, { useState } from 'react';
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Alert, Divider, Link,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Typography, Accordion, AccordionSummary, AccordionDetails, Tooltip, LinearProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaceIcon from '@mui/icons-material/Place';
import { apiService } from '../../services/api';

/**
 * Perfil de uso del vehículo: dónde duerme, a dónde va y a qué hora.
 *
 * Sirve para ir a buscar un equipo que dejó de reportar. No se carga solo: son
 * miles de posiciones y varias consultas de geocodificación.
 *
 * Dos cosas que la pantalla debe dejar claras, porque se malinterpretaron en uso
 * real: el porcentaje es qué PARTE de la hora está ahí (no en cuántos días pasó
 * por ahí), y el barrio es contexto — quien va a un sitio usa la coordenada.
 */

interface Props {
  deviceId: string;
}

const COLOR_TIPO = (tipo: string): 'success' | 'warning' | 'info' | 'default' => {
  if (tipo?.startsWith('duerme')) return 'success';
  if (tipo?.startsWith('pernoctó')) return 'warning';
  if (tipo === 'permanencia larga') return 'info';
  return 'default';
};

/** Barras de presencia por hora: dice a qué hora ir. */
const Barras: React.FC<{ datos: any[]; titulo: string }> = ({ datos, titulo }) => {
  const conDatos = datos?.filter((h) => h.porcentaje !== null) || [];
  if (conDatos.length < 4) return null;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">{titulo}</Typography>
      <Box sx={{ display: 'flex', gap: 0.4, alignItems: 'flex-end', height: 78, overflowX: 'auto' }}>
        {datos.map((h) => {
          const pct = h.porcentaje;
          const sin = pct === null;
          return (
            <Box key={h.hora} sx={{ flex: '1 0 17px', textAlign: 'center' }}
              title={sin ? `${h.etiqueta}: sin datos suficientes` : `${h.etiqueta}: está en su base el ${pct}% de esa hora`}>
              <Box sx={{
                height: sin ? 3 : Math.max(3, (pct / 100) * 58),
                bgcolor: sin ? 'action.disabledBackground' : pct >= 60 ? 'success.main' : pct >= 35 ? 'warning.main' : 'error.light',
                borderRadius: 0.5,
              }} />
              <Typography variant="caption" sx={{ fontSize: 8, opacity: 0.6 }}>
                {h.hora % 3 === 0 ? h.hora : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const Sitio: React.FC<{ s: any; n: number; destacado?: boolean }> = ({ s, n, destacado }) => (
  <Box sx={{ py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>{n}.</Typography>
      <Link href={s.mapa} target="_blank" rel="noopener"
        sx={{ fontFamily: 'monospace', fontSize: 13, fontWeight: destacado ? 700 : 500 }}>
        {s.lat}, {s.lon}
      </Link>
      <Chip size="small" color={COLOR_TIPO(s.tipo)} label={s.tipo} sx={{ height: 19, fontSize: 10 }} />
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {s.calle
        ? <>Sobre <strong>{s.calle}</strong> (a {s.calleDistanciaM} m){s.barrio ? ` · ${s.barrio}` : ''}</>
        : <>{s.barrio || 'sin referencia'} — <em>ninguna calle nombrada a menos de 40 m</em></>}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.3 }}>
      {s.paradas} paradas en {s.dias} días · se queda <strong>{s.medianaMin} min</strong> típicos
      {s.pernoctas > 0 && <> · <strong>{s.pernoctas}</strong> noches</>}
      {s.estadiasLargas > 0 && <> · {s.estadiasLargas} estadías de +2h</>}
      {' · '}{s.horasTotal} h en total
    </Typography>
  </Box>
);

const PerfilDeUso: React.FC<Props> = ({ deviceId }) => {
  const [perfil, setPerfil] = useState<any>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dias, setDias] = useState(45);
  const [progreso, setProgreso] = useState(0);
  const [paso, setPaso] = useState('');

  // Se arranca el cálculo y se pregunta por el avance cada dos segundos. Una sola
  // petición de minuto y medio se cae en el camino; así además se ve el progreso.
  const correr = async (d = dias) => {
    setCargando(true);
    setError(null);
    setProgreso(0);
    setPaso('Preparando…');
    try {
      const { jobId } = await apiService.iniciarPatron(deviceId, d);
      let fallosSeguidos = 0;
      for (let i = 0; i < 150; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const e = await apiService.estadoPatron(deviceId, jobId);
          fallosSeguidos = 0;
          setProgreso(e.progreso || 0);
          setPaso(e.paso || '');
          if (e.estado === 'listo') { setPerfil(e.data); setCargando(false); return; }
        } catch (err: any) {
          // Un fallo suelto de red no debe tumbar el intento: el cálculo sigue
          // corriendo en el servidor. Solo se abandona si falla varias veces.
          if (err?.response?.status === 404) throw err;
          if (++fallosSeguidos >= 5) throw err;
        }
      }
      throw new Error('El cálculo está tardando más de lo normal. Vuelve a intentarlo.');
    } catch (e: any) {
      setError(e?.response?.data?.message || e.message || 'No se pudo construir el perfil');
    }
    setCargando(false);
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Perfil de uso</Typography>
            <Typography variant="body2" color="text.secondary">
              Dónde duerme, a dónde va y a qué hora. Para encontrar el vehículo cuando el equipo dejó de reportar.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[15, 45, 90].map((d) => (
              <Button key={d} size="small" variant={dias === d ? 'contained' : 'outlined'}
                onClick={() => { setDias(d); if (perfil) correr(d); }} disabled={cargando}>
                {d} días
              </Button>
            ))}
            <Button variant="contained" onClick={() => correr()} disabled={cargando}>
              {cargando ? <CircularProgress size={20} /> : perfil ? 'Actualizar' : 'Construir perfil'}
            </Button>
          </Box>
        </Box>

        {!perfil && !cargando && !error && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Se construye solo cuando lo pides. Analiza miles de posiciones y verifica los nombres de
            calle contra el mapa, así que tarda alrededor de un minuto.
          </Alert>
        )}

        {cargando && (
          <Box sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{paso}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{progreso}%</Typography>
            </Box>
            <LinearProgress variant={progreso > 0 ? 'determinate' : 'indeterminate'} value={progreso} />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Tarda alrededor de minuto y medio. Puedes seguir usando el portal mientras tanto.
            </Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        {perfil && !perfil.suficiente && <Alert severity="warning" sx={{ mt: 2 }}>{perfil.mensaje}</Alert>}

        {perfil?.suficiente && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              {perfil.posiciones.toLocaleString('es-DO')} posiciones · {perfil.paradasAnalizadas} paradas ·{' '}
              {perfil.diasConDatos} días · {perfil.periodo.desde} → {perfil.periodo.hasta}
            </Typography>

            {perfil.conclusion && (
              <Alert severity={perfil.conclusion.lugarFijoDeTrabajo ? 'info' : 'success'} sx={{ mb: 2 }}>
                {perfil.conclusion.texto}
              </Alert>
            )}

            {/* Base */}
            {perfil.base && (
              <Box sx={{ p: 2, mb: 2, borderLeft: 4, borderColor: 'primary.main', bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                  <PlaceIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Dónde pasa las noches</Typography>
                  <Chip size="small"
                    color={perfil.base.certezaNocturna >= 90 ? 'success' : perfil.base.certezaNocturna >= 60 ? 'warning' : 'default'}
                    label={`${perfil.base.certezaNocturna}% de certeza`} />
                  {perfil.base.esSuposicion && <Chip size="small" color="warning" label="es una suposición" />}
                </Box>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 14, fontWeight: 700 }}>
                  <Link href={perfil.base.mapa} target="_blank" rel="noopener">
                    {perfil.base.lat}, {perfil.base.lon}
                  </Link>
                </Typography>
                <Typography variant="body2">
                  {perfil.base.calle
                    ? <>Sobre <strong>{perfil.base.calle}</strong> (a {perfil.base.calleDistanciaM} m){perfil.base.barrio ? ` · ${perfil.base.barrio}` : ''}</>
                    : <>{perfil.base.barrio || 'sin referencia'} — ninguna calle nombrada a menos de 40 m</>}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Durmió ahí <strong>{perfil.base.pernoctas}</strong> de {perfil.base.dias} días ·{' '}
                  {perfil.base.paradas} paradas · se queda {perfil.base.medianaMin} min típicos ·{' '}
                  {perfil.base.estadiasLargas} estadías de más de 2 h.
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Sobre {perfil.base.lecturasNocturnas.toLocaleString('es-DO')} lecturas nocturnas.
                  El GPS dispersa unos {perfil.base.precisionAproximadaM} m: el punto señala esa casa o la de al lado.
                </Typography>
              </Box>
            )}

            {/* Cuándo ir */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>A qué hora encontrarlo ahí</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                El porcentaje es qué parte de esa hora está en su base con el motor apagado.
              </Typography>
              {perfil.mejoresHorasLaborables?.length > 0 && (
                <Box sx={{ mt: 0.8 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Dentro de horario laborable (7 AM – 6 PM):</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                    {perfil.mejoresHorasLaborables.map((h: any) => (
                      <Chip key={h.hora} size="small" label={`${h.hora} · ${h.probabilidad}%`}
                        color={h.probabilidad >= 60 ? 'success' : 'default'} />
                    ))}
                  </Box>
                </Box>
              )}
              {perfil.mejoresHoras?.length > 0 && (
                <Box sx={{ mt: 0.8 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>A cualquier hora:</Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                    {perfil.mejoresHoras.map((h: any) => (
                      <Chip key={h.hora} size="small" label={`${h.hora} · ${h.probabilidad}%`}
                        color={h.probabilidad >= 90 ? 'success' : 'default'} />
                    ))}
                  </Box>
                </Box>
              )}
              <Barras datos={perfil.presenciaPorHora} titulo="Lunes a viernes" />
              <Barras datos={perfil.presenciaFinDeSemana} titulo="Sábado y domingo" />
            </Box>

            {/* Rutina */}
            {perfil.resumenRutina && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Rutina</Typography>
                <Typography variant="body2">
                  Sale típicamente a las <strong>{perfil.resumenRutina.saleTipico || '—'}</strong>
                  {perfil.resumenRutina.saleEntre && ` (entre ${perfil.resumenRutina.saleEntre})`} y vuelve entre{' '}
                  <strong>{perfil.resumenRutina.vuelveEntre || '—'}</strong>.
                  {' '}Salió {perfil.resumenRutina.diasConSalida} de {perfil.resumenRutina.diasAnalizados} días.
                  {perfil.resumenRutina.trabajaFinDeSemana && ' También sábados y domingos.'}
                </Typography>
              </>
            )}

            {/* Otros sitios */}
            {perfil.zonas?.length > 0 && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Otros lugares donde se detiene
                </Typography>
                {perfil.zonas.map((z: any, i: number) => <Sitio key={i} s={z} n={i + 2} />)}
              </>
            )}

            {/* Última posición */}
            {perfil.ultimaPosicion && (
              <Alert severity={perfil.equipo.reportando ? 'success' : 'warning'} sx={{ mt: 2 }}>
                <strong>Última posición:</strong> {perfil.ultimaPosicion.cuando} — a{' '}
                {perfil.ultimaPosicion.aLaBaseM} m de su base, a {perfil.ultimaPosicion.velocidad} km/h.{' '}
                <Link href={perfil.ultimaPosicion.mapa} target="_blank" rel="noopener">ver en el mapa</Link>
              </Alert>
            )}

            {/* Día por día */}
            <Accordion disableGutters sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Día por día ({perfil.rutina.length})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer sx={{ overflowX: 'auto', maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Día</TableCell>
                        <TableCell>Primera salida</TableCell>
                        <TableCell>Último regreso</TableCell>
                        <TableCell align="right">Salidas</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {perfil.rutina.map((d: any, i: number) => (
                        <TableRow key={i} sx={d.esFinde ? { bgcolor: 'action.hover' } : undefined}>
                          <TableCell>{d.fecha}</TableCell>
                          <TableCell>{d.primeraSalida || '—'}</TableCell>
                          <TableCell>{d.ultimoRegreso || '—'}</TableCell>
                          <TableCell align="right">{d.viajes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>

            {/* Advertencias */}
            {perfil.advertencias?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                {perfil.advertencias.map((a: string, i: number) => (
                  <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>· {a}</Typography>
                ))}
                <Tooltip title={perfil.fuente}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textDecoration: 'underline dotted' }}>
                    · De dónde salen estos datos
                  </Typography>
                </Tooltip>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default PerfilDeUso;
